/**
 * Picking playback back up after something outside the page interrupted it.
 *
 * A phone that goes to sleep takes the connection the audio is streaming over
 * with it: Wi-Fi drops to power save, the in-flight request to the object store
 * dies, and the element fires `error` or simply stops advancing. Recovery used
 * to be one manifest refresh and, if that failed, a message telling the listener
 * to reload the page — and it fails, because the dead radio that killed the
 * audio kills the fetch as well. The owner's Odyssey stopped 45.9 s into BOOK I
 * the moment the screen went off and stayed stopped (2026-09-20).
 *
 * So recovery is a loop rather than a single shot, and the arithmetic in it is
 * here so it can be tested without a media element.
 */

/**
 * RETRY_DELAYS_MS is how long to wait before each attempt, then every attempt
 * after them.
 *
 * The first is short because the commonest interruption is a dropped socket
 * that the next request just succeeds on; the tail is long because the second
 * commonest is a phone with no network at all, and asking it twice a second for
 * four minutes drains a battery to find out what one ask a quarter-minute later
 * would have told us.
 */
export const RETRY_DELAYS_MS = [400, 1_000, 2_500, 6_000, 15_000] as const;

/** retryDelay is the wait before the attempt after `attempt` have been made. */
export function retryDelay(attempt: number): number {
  const at = Math.min(Math.max(Math.floor(attempt), 0), RETRY_DELAYS_MS.length - 1);
  return RETRY_DELAYS_MS[at]!;
}

/**
 * ATTEMPTS_BEFORE_TELLING is how many attempts pass quietly.
 *
 * A listener whose audio comes back four seconds later was never interested in
 * knowing it went away, and a banner that appears and disappears on every
 * dropped socket trains them to ignore it.
 */
export const ATTEMPTS_BEFORE_TELLING = 4;

/**
 * MAX_ATTEMPTS is where trying stops and the listener is asked.
 *
 * About four and a half minutes with the delays above. Past that the phone is
 * not coming back on its own, and a page that retries for ever is a page that
 * is still retrying tomorrow. Coming back on the network or to the screen
 * resets the count, so this is a ceiling on one unattended stretch, not on the
 * session.
 */
export const MAX_ATTEMPTS = 20;

/**
 * EXPIRY_MARGIN_MS is how close to expiry a manifest's links count as spent.
 *
 * They are signed for at least fifteen minutes (storage.SignedURLTTL), and a
 * link that outlives its signature mid-recovery costs the whole retry.
 */
export const EXPIRY_MARGIN_MS = 60_000;

/**
 * linksStale says whether a manifest's signed URLs are due to run out.
 *
 * An unparseable time counts as stale: a manifest we cannot date is one we
 * cannot trust the links of, and re-minting them costs one cheap request.
 */
export function linksStale(expiresAt: string | undefined, now: number): boolean {
  const at = Date.parse(expiresAt ?? "");
  if (!Number.isFinite(at)) return true;
  return at - now <= EXPIRY_MARGIN_MS;
}

/**
 * sourceAt is a part's URL that starts where playback stopped.
 *
 * A media fragment rather than setting `currentTime` once the element has
 * loaded, and the reason is the bug before this one: a hidden page loads no
 * media at all until something asks it to play, so anything that waits for
 * metadata before calling `play()` waits for ever with the screen off. The
 * fragment travels with the source, so one assignment and one `play()` resume
 * mid-part without waiting for anything — and without the audible jump back to
 * the start of the part that setting `currentTime` afterwards leaves.
 *
 * The fragment is not sent to the server, so a signed URL keeps its signature.
 */
export function sourceAt(url: string, offsetMs: number): string {
  const bare = url.split("#")[0] ?? url;
  if (!(offsetMs > 0)) return bare;
  return `${bare}#t=${(offsetMs / 1000).toFixed(3)}`;
}
